//#region IMPORT
import React, { useState } from 'react';
import Global from '../../Services/Common/Global';
import Commons from '../../Services/Common/Common';
import { useSelector } from 'react-redux';
import HwpPreview from './HwpPreview';
//#endregion

const HWP_EXTS = ['hwp', 'hwpx'];

/**
 * 파일 미리보기 컴포넌트
 * @param {Array} [props.selectedRowData] - 선택된 행 데이터 배열 (selectedRowData가 있으면 첫 번째 행 사용)
 * @param {string} [props.fileNameField] - 파일명 필드명 (예: 'MACHINE_IMAGE_FILE_NAME', 'JOURNEYMAN_FILE_NAME')
 * @param {string} [props.filePathField] - 파일 경로 필드명 (예: 'MACHINE_IMAGE_FILE_PATH', 'JOURNEYMAN_FILE_PATH')
 * @param {string} [props.filePreviewHeight='600px'] - 파일 미리보기 높이
 * @returns {JSX.Element} 미리보기 컴포넌트
 */
function FilePreview({
  selectedRowData,
  fileNameField,
  filePathField,
  filePreviewHeight = '600px',
}) {
  const { previewFilePathField, previewUrl, previewFileName } = useSelector(
    (state) => state.fileField
  );
  // rhwp 파싱에 실패한 경로. 기존 PDF 변환본으로 폴백하기 위해 기억한다.
  const [hwpErrorPath, setHwpErrorPath] = useState(null);
  const rowData = selectedRowData?.[0];

  if (!rowData) {
    return <div>파일 정보가 없습니다.</div>;
  }

  // Redux의 previewUrl이 새로 업로드한 파일인 경우 (PreviewFiles 경로 또는 base64) 우선 사용
  const isNewUpload =
    previewUrl &&
    (previewUrl.includes('PreviewFiles') || previewUrl.startsWith('data:'));

  if (isNewUpload && previewFileName) {
    const ext = previewFileName.split('.').pop()?.toLowerCase();

    // 이미지 파일 처리 (base64 또는 URL)
    if (['png', 'jpg', 'jpeg'].includes(ext)) {
      return (
        <img
          src={previewUrl}
          alt="미리보기"
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      );
    }

    // 한글 파일 처리 (원본을 rhwp로 직접 렌더링)
    if (
      HWP_EXTS.includes(ext) &&
      !previewUrl.startsWith('data:') &&
      hwpErrorPath !== previewUrl
    ) {
      return (
        <HwpPreview
          filePath={previewUrl}
          fileName={previewFileName}
          height={filePreviewHeight}
          onError={() => setHwpErrorPath(previewUrl)}
        />
      );
    }

    // PDF 및 Office 파일 처리
    if (
      ['pdf', 'hwp', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(ext)
    ) {
      const pdfUrl =
        ext !== 'pdf'
          ? previewUrl.replace(/\.(hwp|doc|docx|ppt|pptx|xls|xlsx)$/i, '.pdf')
          : previewUrl;

      return (
        <iframe
          src={pdfUrl}
          width="100%"
          height={filePreviewHeight}
          title="PDF 미리보기"
          allow="fullscreen"
        />
      );
    }
  }


  // 미리보기가 있으면 미리보기 파일 경로, 없으면 기존 파일 경로
  const finalFilePath =
    (previewFilePathField && rowData?.[previewFilePathField]) ||
    (filePathField && rowData?.[filePathField]);
  const finalFileName = fileNameField && rowData?.[fileNameField];

  if (!finalFileName || !finalFilePath) {
    return <div>파일 정보가 없습니다.</div>;
  }

  // Blob URL인 경우 직접 사용
  if (finalFilePath.startsWith('blob:')) {
    const ext = finalFileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') {
      return (
        <iframe
          src={finalFilePath}
          width="100%"
          height={filePreviewHeight}
          title="PDF 미리보기"
          allow="fullscreen"
        />
      );
    }
  }

  const normalizedFilePath = finalFilePath.startsWith('http')
    ? finalFilePath.replace(Global.file.useUrl + 'files/', '')
    : finalFilePath;

  const ext = finalFileName.split('.').pop()?.toLowerCase();
  const fullUrl =
    Global.file.useUrl + 'files/' + Commons.EncodeUrl(normalizedFilePath);

  // 이미지 파일 처리
  if (['png', 'jpg', 'jpeg'].includes(ext)) {
    return (
      <img
        src={fullUrl}
        alt="미리보기"
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
      />
    );
  }

  // 한글 파일 처리 (원본을 rhwp로 직접 렌더링)
  if (HWP_EXTS.includes(ext) && hwpErrorPath !== normalizedFilePath) {
    return (
      <HwpPreview
        filePath={normalizedFilePath}
        fileName={finalFileName}
        height={filePreviewHeight}
        onError={() => setHwpErrorPath(normalizedFilePath)}
      />
    );
  }

  // PDF 및 Office 파일 처리
  if (
    ['pdf', 'hwp', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(ext)
  ) {
    const pdfUrl =
      ext !== 'pdf'
        ? fullUrl.replace(/\.(hwp|doc|docx|ppt|pptx|xls|xlsx)$/i, '.pdf')
        : fullUrl;

    return (
      <iframe
        src={pdfUrl}
        width="100%"
        height={filePreviewHeight}
        title="PDF 미리보기"
        allow="fullscreen"
      />
    );
  }

  return <div>지원하지 않는 형식입니다.</div>;
}

export default FilePreview;
